import React, { useEffect, useRef, useState } from 'react';
import { BeakerIcon, MagnifyingGlassIcon, TrashIcon } from '@heroicons/react/24/outline';
import billingService from '../../services/billingService';
import nappiService from '../../services/nappiService';
import { errText } from '../../services/scheduleService';
import { isAdmin } from '../../utils/permissions';
import { Issue, LineDiagnoses, rands } from './TariffLines';

const TYPE = { medicine: 'Medicine', consumable: 'Consumable' };

// Type words or the start of a NAPPI code; pick from the practice's list.
const NappiPicker = ({ onPick, disabled }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setRows([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const found = await nappiService.list({ q: text });
        if (!cancelled) { setRows(found); setOpen(true); }
      } catch {
        if (!cancelled) setRows([]);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={box} className="relative">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => rows.length && setOpen(true)}
        disabled={disabled}
        placeholder="Add a medicine or consumable — name or NAPPI code…"
        className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30 disabled:bg-slate-50"
      />
      {open && q.trim().length >= 2 && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {rows.map((r) => (
            <li key={r.item_id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(r); setQ(''); setRows([]); setOpen(false); }}
                className="flex w-full items-start gap-3 px-3 py-1.5 text-left text-sm hover:bg-teal-50"
              >
                <span className="w-20 shrink-0 font-mono font-semibold text-brand-navy">{r.nappi_code}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-slate-800">{r.description}{r.strength ? ` ${r.strength}` : ''}</span>
                  <span className="block text-[11px] text-slate-400">
                    {TYPE[r.item_type]} · {rands(r.unit_price)} per {r.unit}{r.source === 'placeholder' ? ' · demo item' : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
          {rows.length === 0 && <li className="px-3 py-2 text-xs text-slate-400">Not in the practice’s list — an administrator can add it in Codes &amp; Tariffs.</li>}
        </ul>
      )}
    </div>
  );
};

const toPayload = (items) => items.map((i) => ({
  item_no: i.item_no, quantity: i.quantity, unit_price: i.unit_price, icd10_codes: i.icd10_all ? null : i.diagnoses,
}));

/**
 * ItemLines — medicines and consumables on the invoice, by NAPPI code. Editable while
 * the invoice may be changed: add from the practice's list, change the quantity, remove,
 * and (billing team) correct the price. Each line is claimed under ICD-10 codes like a
 * tariff line. Shows the invoice total (fees + items) underneath.
 */
const ItemLines = ({ draft, onDraft }) => {
  const editable = !!draft.permissions?.edit;
  const admin = isAdmin();
  const items = draft.items || [];
  const [edits, setEdits] = useState({}); // item_no → { quantity, unit_price } being typed
  const [dxOpen, setDxOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const coded = draft.lines.filter((l) => l.code);

  useEffect(() => { setEdits({}); }, [draft]);

  const save = async (next) => {
    setBusy(true);
    setErr('');
    try {
      onDraft(await billingService.setItems(draft.draft_id, next));
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = (itemNo) => {
    const e = edits[itemNo];
    if (!e) return;
    const next = toPayload(items).map((p) => (p.item_no === itemNo ? {
      ...p,
      quantity: e.quantity !== undefined ? Number(e.quantity) : p.quantity,
      unit_price: e.unit_price !== undefined ? Number(e.unit_price) : p.unit_price,
    } : p));
    save(next);
  };
  const setEdit = (itemNo, k, v) => setEdits((x) => ({ ...x, [itemNo]: { ...x[itemNo], [k]: v } }));
  const cell = 'w-20 rounded-md border border-slate-300 px-2 py-1 text-right text-sm tabular-nums focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

  if (!items.length && !editable) return null;

  return (
    <div className={`border-t border-slate-200 ${busy ? 'pointer-events-none opacity-60' : ''}`}>
      <div className="flex items-center gap-2 px-4 pb-2 pt-3">
        <BeakerIcon className="h-4 w-4 text-slate-400" />
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Medicines &amp; consumables</h3>
      </div>
      {editable && (
        <div className="mx-4 mb-2">
          <NappiPicker disabled={busy} onPick={(row) => save([...toPayload(items), { item_id: row.item_id, quantity: 1 }])} />
        </div>
      )}
      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-slate-100 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="w-28 px-4 py-1.5 font-semibold">NAPPI</th>
                <th className="px-2 py-1.5 font-semibold">Item</th>
                <th className="w-24 px-2 py-1.5 text-right font-semibold">Qty</th>
                <th className="w-28 px-2 py-1.5 text-right font-semibold">Unit price</th>
                <th className="w-28 px-4 py-1.5 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it) => {
                const e = edits[it.item_no] || {};
                return (
                  <tr key={it.item_no} className="align-top">
                    <td className="py-2 pl-4 pr-2">
                      <div className="font-mono font-bold text-brand-navy">{it.nappi_code}</div>
                      <div className="text-[10px] font-semibold uppercase text-slate-400">{TYPE[it.item_type]}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1 text-slate-800">{it.description}</div>
                        {editable && (
                          <button onClick={() => save(toPayload(items).filter((p) => p.item_no !== it.item_no))} title="Remove this line" className="shrink-0 rounded p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-600">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {Array.isArray(it.diagnoses) && (
                        <LineDiagnoses
                          line={it}
                          diagnoses={coded}
                          editing={dxOpen === it.item_no}
                          onEdit={editable ? () => setDxOpen(dxOpen === it.item_no ? null : it.item_no) : undefined}
                          onSave={(codes) => { setDxOpen(null); save(toPayload(items).map((p) => (p.item_no === it.item_no ? { ...p, icd10_codes: codes } : p))); }}
                        />
                      )}
                      {it.issues?.length > 0 && <div className="mt-1 space-y-0.5">{it.issues.map((i) => <Issue key={i.message} issue={i} />)}</div>}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {editable ? (
                        <input
                          type="number" min="0.01" step="any" className={cell}
                          value={e.quantity ?? it.quantity}
                          onChange={(ev) => setEdit(it.item_no, 'quantity', ev.target.value)}
                          onBlur={() => commit(it.item_no)}
                          onKeyDown={(ev) => { if (ev.key === 'Enter') ev.currentTarget.blur(); }}
                        />
                      ) : <span className="tabular-nums text-slate-700">{it.quantity}</span>}
                      <div className="text-[10px] text-slate-400">{it.unit}</div>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {editable && admin ? (
                        <input
                          type="number" min="0" step="0.01" className={cell}
                          value={e.unit_price ?? it.unit_price}
                          onChange={(ev) => setEdit(it.item_no, 'unit_price', ev.target.value)}
                          onBlur={() => commit(it.item_no)}
                          onKeyDown={(ev) => { if (ev.key === 'Enter') ev.currentTarget.blur(); }}
                        />
                      ) : <span className="tabular-nums text-slate-500">{rands(it.unit_price)}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right font-semibold tabular-nums text-slate-800">{rands(it.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t border-slate-200 text-sm">
              <tr>
                <td colSpan={4} className="px-2 pt-2 text-right text-slate-500">Medicines &amp; consumables (VAT incl.)</td>
                <td className="px-4 pt-2 text-right tabular-nums text-slate-700">{rands(draft.items_total)}</td>
              </tr>
              <tr>
                <td colSpan={4} className="px-2 pb-2 text-right font-bold text-brand-deep">Invoice total</td>
                <td className="whitespace-nowrap px-4 pb-2 text-right text-base font-bold tabular-nums text-brand-deep">{rands(draft.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {err && <p className="px-4 pb-2 text-sm text-red-600">{err}</p>}
    </div>
  );
};

export default ItemLines;
